/**
 * GroupLivePage — Entry component for VER-56 Group Assessment Model A.
 *
 * Orchestrates the practitioner-facing flow through three phases:
 *   1. Initiate  → Practitioner sets up and starts the session
 *   2. Roster    → Live participant check-in (with simulated updates in mock mode)
 *   3. Aggregate → Anonymous group results after session closes
 *
 * The participant-facing join flow (GroupLiveJoin) lives on a separate public
 * route — this page is the practitioner dashboard only.
 *
 * Route (to be added by orchestrator):
 *   /assess/group-live/:assessmentId   (requires auth + RBAC permit)
 *
 * TODOs:
 * - TODO [RBAC]: Add the correct permit code to the route guard in Routes.tsx
 *   once defined. Run verbal-rbac-review before merge.
 * - TODO [ROUTING]: The orchestrator should wire this as:
 *   <Route path="/assess/group-live/:assessmentId" element={<GroupLivePage />} />
 *   and pass the assessmentId via useParams() to seed the initiate form.
 * - TODO [VER-55]: Once the session POST returns a real session ID, persist it
 *   and use it for all subsequent API calls.
 */

import React, { useState } from "react";
import { GroupLiveSession, MOCK_COMPLETE_SESSION } from "./groupLive.mock";
import { GroupLiveInitiate } from "./GroupLiveInitiate";
import { GroupLiveRoster } from "./GroupLiveRoster";
import { GroupLiveAggregate } from "./GroupLiveAggregate";

// ---------------------------------------------------------------------------
// View states
// ---------------------------------------------------------------------------

type GroupLiveView = "initiate" | "roster" | "aggregate";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupLivePage = (): JSX.Element => {
    const [view, setView] = useState<GroupLiveView>("initiate");
    const [session, setSession] = useState<GroupLiveSession | null>(null);

    const handleSessionStarted = (newSession: GroupLiveSession) => {
        setSession(newSession);
        setView("roster");
    };

    const handleSessionClosed = (closedSession: GroupLiveSession) => {
        // In mock mode, attach a complete aggregate from the fixture.
        // TODO [VER-55]: In production, the aggregate is fetched from
        //   GET /api/sessions/:id/aggregate after the close POST completes.
        const withAggregate: GroupLiveSession = {
            ...closedSession,
            status: "complete",
            aggregate: MOCK_COMPLETE_SESSION.aggregate,
        };
        setSession(withAggregate);
        setView("aggregate");
    };

    if (view === "initiate") {
        return <GroupLiveInitiate onSessionStarted={handleSessionStarted} />;
    }

    if (view === "roster" && session !== null) {
        return <GroupLiveRoster session={session} onSessionClosed={handleSessionClosed} />;
    }

    if (view === "aggregate" && session !== null) {
        return <GroupLiveAggregate session={session} />;
    }

    // Fallback — should not happen in normal flow
    return <GroupLiveInitiate onSessionStarted={handleSessionStarted} />;
};

export default GroupLivePage;
